import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Wand2, TrendingUp, Clock, ChevronLeft, ChevronRight } from "lucide-react";

import { Header } from "../layout/Header";
import { CategorySidebar, MobileCategorySidebar } from "../layout/CategorySidebar";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import FeedList from "../feed/FeedList";
import { useAppHeaderProps } from "../../lib/useAppHeaderProps";
import { CATEGORIES } from "../../lib/categories";
import { useUrlCategory, useUrlPage, useUrlParam } from "../../lib/url-state";
import { ShareLinkButton } from "../layout/ShareLinkButton";
import { getStoredCurrentUser } from "../../lib/app-events";
import ApiService from "../../services/api-service";
import { showErrorToast, showSuccessToast } from "../../lib/toast";
import { normalizePointsCount } from "../../lib/format";
import { ITEMS_PER_PAGE } from "../../constants/app";
import { AI_PROMPT_COMPLEXITY_OPTIONS, AI_PROMPT_COMPLEXITY_META, AiPromptComplexity } from "../../constants/feed-meta";
import { FeedItem, Vote, Comment } from "../../types/feed";
import { CurrentUser } from "../../types/user";

type ComplexityFilterValue = "all" | AiPromptComplexity;
const COMPLEXITY_FILTER_OPTIONS: readonly ComplexityFilterValue[] = ["all", "beginner", "intermediate", "advanced"];

type SortOption = "trending" | "recent";
const SORT_OPTIONS: readonly SortOption[] = ["trending", "recent"];

// Same filled-pill chip styling as the home feed's ContentTypeFilter.
const filterChipClass = (isActive: boolean) =>
  `flex items-center gap-1.5 shrink-0 rounded-full px-3.5 lg:px-3 whitespace-nowrap transition-all duration-200 ${
    isActive
      ? "bg-slate-900 text-white shadow-sm shadow-slate-900/20 hover:bg-slate-900 hover:text-white dark:bg-white dark:text-slate-900 dark:hover:bg-white dark:hover:text-slate-900"
      : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-600 dark:bg-slate-800/80 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-300"
  }`;

// Filter row: "All" clears the filter (spec requirement), plus one chip per
// complexity level - the same chips a prompt card's badge links into.
function ComplexityFilter({ value, onChange }: { value: ComplexityFilterValue; onChange: (value: ComplexityFilterValue) => void }) {
  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar sm:flex-wrap sm:overflow-visible -mx-3 px-3 sm:mx-0 sm:px-0 py-0.5">
      <Button variant="ghost" size="sm" onClick={() => onChange("all")} className={filterChipClass(value === "all")}>
        <Wand2 className="w-3.5 h-3.5" />
        <span className="text-xs">All Levels</span>
      </Button>
      {AI_PROMPT_COMPLEXITY_OPTIONS.map((option) => (
        <Button
          key={option.value}
          variant="ghost"
          size="sm"
          onClick={() => onChange(option.value as ComplexityFilterValue)}
          className={filterChipClass(value === option.value)}
        >
          <span className="text-xs">{option.label}</span>
        </Button>
      ))}
    </div>
  );
}

function AiPromptsSortToggle({ value, onChange, disabled }: { value: SortOption; onChange: (value: SortOption) => void; disabled?: boolean }) {
  const meta: Record<SortOption, { label: string; Icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }> = {
    trending: { label: "Trending", Icon: TrendingUp },
    recent: { label: "Recent", Icon: Clock },
  };
  const ActiveIcon = meta[value].Icon;
  const itemClassName =
    "focus:bg-slate-100 dark:focus:bg-slate-700 data-[state=checked]:bg-slate-800 data-[state=checked]:text-white dark:data-[state=checked]:bg-slate-200 dark:data-[state=checked]:text-slate-900 data-[state=checked]:focus:bg-slate-800 dark:data-[state=checked]:focus:bg-slate-200";

  return (
    <Select value={value} onValueChange={(next: string) => onChange(next as SortOption)} disabled={disabled}>
      <SelectTrigger
        className="h-9 w-[136px] shrink-0 rounded-md bg-slate-800 text-white border-slate-800 transition-all duration-200 hover:bg-slate-700 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200 dark:hover:bg-slate-300"
        aria-label="Sort AI Prompts"
      >
        <span className="flex items-center gap-2">
          <ActiveIcon className="size-4 text-current" aria-hidden="true" />
          <SelectValue>{meta[value].label}</SelectValue>
        </span>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="trending" className={itemClassName}>
          <span className="flex items-center gap-2">
            <TrendingUp className="size-4 text-current" />
            Trending
          </span>
        </SelectItem>
        <SelectItem value="recent" className={itemClassName}>
          <span className="flex items-center gap-2">
            <Clock className="size-4 text-current" />
            Recent
          </span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

function AiPromptsPagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages: number[] = [];
    const visiblePages = 5;
    let start = Math.max(1, currentPage - Math.floor(visiblePages / 2));
    let end = Math.min(totalPages, start + visiblePages - 1);
    if (end - start < visiblePages - 1) start = Math.max(1, end - visiblePages + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="flex items-center justify-center gap-1 mt-8 select-none">
      <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="w-8 h-8 rounded border-slate-300 dark:border-slate-600">
        <ChevronLeft className="w-3 h-3" />
      </Button>
      {getPageNumbers().map((page) => (
        <Button
          key={page}
          variant={page === currentPage ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(page)}
          className={`w-8 h-8 rounded transition-all duration-150 ${
            page === currentPage
              ? "bg-slate-800 text-white border-slate-800 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200"
              : "border-slate-300 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-700"
          }`}
        >
          {page}
        </Button>
      ))}
      <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="w-8 h-8 rounded border-slate-300 dark:border-slate-600">
        <ChevronRight className="w-3 h-3" />
      </Button>
    </div>
  );
}

// /ai-prompts?complexity=beginner - every standalone AI Prompt, filterable by
// complexity level. The filter lives in the URL so refresh, back/forward and
// shared links all keep it (see lib/url-state's useUrlParam).
export function AiPromptsPage() {
  const { onOpenAuth, onOpenCreateModal } = useAppHeaderProps();

  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(getStoredCurrentUser);
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [activeCategory, setActiveCategory, getCategoryHref] = useUrlCategory();
  const [complexity, setComplexity] = useUrlParam<ComplexityFilterValue>("complexity", "all", COMPLEXITY_FILTER_OPTIONS);
  const [sort, setSort] = useUrlParam<SortOption>("sort", "trending", SORT_OPTIONS);
  const [currentPage, setCurrentPage] = useUrlPage();

  useEffect(() => {
    document.title = "AI Prompts · The Deepam";
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleUserChange = (event: Event) => {
      setCurrentUser(event instanceof CustomEvent ? event.detail ?? null : getStoredCurrentUser());
    };
    window.addEventListener("inspirelens:userchange", handleUserChange);
    return () => window.removeEventListener("inspirelens:userchange", handleUserChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    ApiService.getAiPrompts(complexity === "all" ? undefined : complexity)
      .then((res: { posts?: FeedItem[] }) => {
        if (!cancelled) setItems(res.posts || []);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [complexity, retryToken]);

  const handleVote = useCallback(
    (id: number, delta: number, updatedVotes?: Vote[], postType?: string, absolutePoints?: number, updatedTimestamp?: string | number) => {
      setItems((prev) =>
        prev.map((item) => {
          if (item.id !== id || (postType && item.type !== postType)) return item;
          const oldPoints = normalizePointsCount(item.points_count);
          const nextPoints =
            typeof absolutePoints === "number" && Number.isFinite(absolutePoints) ? absolutePoints : oldPoints + delta;
          const updatedItem: FeedItem = { ...item, points_count: nextPoints, points: updatedVotes || item.points };
          if (updatedTimestamp) {
            updatedItem.updated_at = updatedTimestamp;
            updatedItem.updatedAt = updatedTimestamp;
          }
          return updatedItem;
        })
      );
    },
    []
  );

  const handleAddComment = useCallback(async (itemId: number, postType: string, comment: any) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const newComment: Comment = {
          id: Date.now(),
          text: comment?.text,
          user: comment?.user,
          user_id: comment?.user_id ?? comment?.userId,
          created_at: Date.now(),
          createdBy: comment?.createdBy,
        };
        return { ...item, comments: [...(Array.isArray(item.comments) ? item.comments : []), newComment] };
      })
    );
  }, []);

  const handleDeleteComment = useCallback((itemId: number, commentId: number) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id !== itemId ? item : { ...item, comments: Array.isArray(item.comments) ? item.comments.filter((c) => c.id !== commentId) : [] }
      )
    );
  }, []);

  const handleEditPost = useCallback((post: FeedItem | number, updatedData: any) => {
    const postId = typeof post === "number" ? post : post?.id;
    if (!postId) return;

    if (!updatedData) {
      setItems((prev) => prev.filter((it) => it.id !== postId));
      showSuccessToast("Post deleted successfully!");
      return;
    }

    setItems((prev) => prev.map((it) => (it.id === postId ? { ...it, ...updatedData } : it)));
    showSuccessToast("Post updated successfully!");
  }, []);

  const handleDeletePost = useCallback(async (postId: number, postType: string) => {
    try {
      const response = await ApiService.deleteContent(postId, postType);
      if (response?.error) throw new Error(response.error);
      setItems((prev) => prev.filter((it) => it.id !== postId));
      showSuccessToast("Post deleted successfully!");
    } catch (err) {
      showErrorToast("Failed to delete post. Please try again.");
      throw err;
    }
  }, []);

  const filteredSorted = useMemo(() => {
    let list = items;
    if (activeCategory !== "all") {
      list = list.filter((item) => String(item.category_id) === String(activeCategory));
    }
    const sorted = [...list];
    if (sort === "trending") {
      sorted.sort((a, b) => normalizePointsCount(b.points_count) - normalizePointsCount(a.points_count));
    } else {
      sorted.sort((a, b) => {
        const ta = new Date(a.updated_at || a.updatedAt || a.created_at || a.createdAt || 0).getTime();
        const tb = new Date(b.updated_at || b.updatedAt || b.created_at || b.createdAt || 0).getTime();
        return tb - ta;
      });
    }
    return sorted;
  }, [items, activeCategory, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / ITEMS_PER_PAGE));
  const paginatedItems = useMemo(
    () => filteredSorted.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [filteredSorted, currentPage]
  );

  const pageTitle = complexity === "all" ? "All levels" : AI_PROMPT_COMPLEXITY_META[complexity].label;

  return (
    <div className="max-w-[85rem] mx-auto px-3 py-6">
      <Header currentUser={currentUser} onOpenAuth={onOpenAuth} onOpenCreateModal={onOpenCreateModal} />

      <div className="mt-6">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Wand2 className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400 shrink-0" aria-hidden="true" />
          AI Prompts
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
          {complexity === "all" ? "Every AI prompt shared on The Deepam." : `${pageTitle} AI prompts shared on The Deepam.`}
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-6">
        <aside className="hidden md:block md:col-span-1">
          <div className="sticky top-6">
            <CategorySidebar activeCategory={activeCategory} onCategoryChange={setActiveCategory} getCategoryHref={getCategoryHref} isLoading={loading} />
          </div>
        </aside>

        <main className="md:col-span-3 space-y-3 sm:space-y-4">
          <div className="flex flex-row flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3">
              <p className="text-slate-600 dark:text-slate-400 text-sm whitespace-nowrap">
                {filteredSorted.length} {filteredSorted.length === 1 ? "item" : "items"}
              </p>
              <MobileCategorySidebar activeCategory={activeCategory} onCategoryChange={setActiveCategory} getCategoryHref={getCategoryHref} isLoading={loading} />
            </div>
            <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
              Page {currentPage} of {totalPages}
            </span>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <ComplexityFilter value={complexity} onChange={setComplexity} />
            <div className="flex items-center gap-2 shrink-0">
              <AiPromptsSortToggle value={sort} onChange={setSort} disabled={loading} />
              <ShareLinkButton title={complexity === "all" ? "AI Prompts" : `AI Prompts · ${pageTitle}`} />
            </div>
          </div>

          {error ? (
            <div className="text-center py-16 space-y-3">
              <p className="text-lg font-medium text-slate-700 dark:text-slate-200">We couldn't load AI Prompts.</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Please try again.</p>
              <Button onClick={() => setRetryToken((t) => t + 1)} variant="outline" className="mt-2">
                Try Again
              </Button>
            </div>
          ) : loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="w-full h-48 rounded-2xl" />
              ))}
            </div>
          ) : paginatedItems.length === 0 ? (
            <div className="text-center py-16 space-y-2">
              <p className="text-lg font-medium text-slate-700 dark:text-slate-200">No AI Prompts here yet.</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {complexity === "all" ? "Be the first to share one." : `No ${pageTitle.toLowerCase()} prompts yet - try a different level.`}
              </p>
            </div>
          ) : (
            <>
              <FeedList
                items={paginatedItems}
                categories={CATEGORIES}
                currentUser={currentUser}
                isLoading={false}
                onVote={handleVote}
                onAddComment={handleAddComment}
                onDeleteComment={handleDeleteComment}
                onEditPost={handleEditPost}
                onDeletePost={handleDeletePost}
                onRequireAuth={() => onOpenAuth("login")}
              />

              <AiPromptsPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default AiPromptsPage;
